// Global Instructions Rule Applied!

import React, { useRef } from 'react'
import { useDrop } from 'react-dnd'
import { portalColors } from '../../../../../portal-configs/colors.js'
import { DropZoneProps } from '../../interfaces.ts'

const ITEM_TYPES = {
  KANBAN_ITEM: 'kanban-item'
} as const

export const DropZone: React.FC<DropZoneProps> = ({
  column,
  targetIndex,
  onMoveItem,
  label,
  isEmpty = false,
  draggedCardHeight = null,
  isLastDropZone = false,
  sameColumnOnly = false
}) => {
  const isReadOnlyColumn = column.dropOption === false
  const ref = useRef<HTMLDivElement>(null)

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ITEM_TYPES.KANBAN_ITEM,
      canDrop: (item: { itemId: string; columnId: string; index: number }) =>
        !isReadOnlyColumn && (!sameColumnOnly || item.columnId === column.id),
      drop: (item: { itemId: string; columnId: string; index: number }) => {
        onMoveItem(item.itemId, item.columnId, column.id, targetIndex)
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop()
      })
    }),
    [column, targetIndex, onMoveItem, sameColumnOnly]
  )

  // Connect the drop ref
  drop(ref)

  const getDropZoneHeight = (): string => {
    if (isEmpty) return 'min-h-[300px]'
    if (isOver && canDrop && draggedCardHeight) {
      return `h-[${draggedCardHeight}px]`
    }
    if (isLastDropZone) {
      return 'h-8'
    }
    return 'h-2'
  }

  // Empty column styling
  if (isEmpty) {
    return (
      <div
        ref={ref}
        className={`flex-1 min-h-[300px] border-2 border-dashed rounded-xl flex items-center justify-center transition-all duration-300 ease-out ${
          isOver && canDrop ? 'scale-105 border-blue-500' : canDrop ? 'border-blue-300 scale-102' : 'border-gray-300'
        }`}
        style={{
          borderColor:
            isOver && canDrop
              ? portalColors.primary[500]
              : canDrop
                ? portalColors.primary[300]
                : portalColors.neutral[300],
          backgroundColor:
            isOver && canDrop
              ? `${portalColors.primary[100]}aa`
              : canDrop
                ? `${portalColors.primary[50]}66`
                : 'transparent',
          boxShadow: isOver && canDrop ? `0 8px 32px ${portalColors.primary[200]}` : 'none'
        }}
      >
        <div className='text-center transition-all duration-300'>
          <div
            className={`text-sm mb-1 font-medium ${isOver && canDrop ? 'scale-110' : ''}`}
            style={{ color: isOver && canDrop ? portalColors.primary[600] : portalColors.neutral[400] }}
          >
            {isReadOnlyColumn
              ? `${column.name} (Read-only)`
              : isOver && canDrop
                ? 'Release to drop here'
                : 'Drop items here'}
          </div>
          <div className='text-xs' style={{ color: portalColors.neutral[300] }}>
            {isReadOnlyColumn ? 'Items cannot be moved in or out' : 'or drag to reorder'}
          </div>
        </div>
      </div>
    )
  }

  // Regular drop zone styling
  return (
    <div
      ref={ref}
      className={`${getDropZoneHeight()} transition-all duration-200 flex items-center justify-center ${
        isOver && canDrop
          ? 'bg-blue-100 border-2 border-dashed border-blue-400 rounded-xl opacity-90'
          : 'bg-transparent hover:bg-blue-50 hover:opacity-50'
      }`}
      style={{
        minHeight: isOver && canDrop ? draggedCardHeight || 8 : isLastDropZone ? 32 : 8,
        marginBottom: isOver && canDrop ? 8 : 0,
        marginTop: isOver && canDrop ? 8 : 0
      }}
      title={label}
    >
      {isOver && canDrop && draggedCardHeight && <span className='text-blue-600 text-xs font-medium'>Drop here</span>}
    </div>
  )
}

export const ITEM_TYPES_EXPORT = ITEM_TYPES
